import React, { useEffect, useState } from 'react';
import { Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography, SelectChangeEvent } from '@mui/material';
import { ItemService } from '../../services/item.service';
import { StockService } from '../../services/stock.service';
import { getStoresByInstitutionId } from '../../services/store.service';
import { Item } from '../../interfaces/item.interface';
import { Store } from '../../interfaces/store.interface';
import { useToast } from '../../context/ToastContext';
import { TEXT_AREA_MAX_LENGTH } from '../../constants/fieldLimits';

const StockOut: React.FC = () => {
    const toast = useToast();
    const [items, setItems] = useState<Item[]>([]);
    const [stores, setStores] = useState<Store[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedItemId, setSelectedItemId] = useState<string>('');
    const [selectedStoreId, setSelectedStoreId] = useState<string>('');
    const [selectedItem, setSelectedItem] = useState<Item | null>(null);
    const [selectedStore, setSelectedStore] = useState<Store | null>(null);
    const [amount, setAmount] = useState<number>(0);
    const [remarks, setRemarks] = useState<string>('');
    const [currentStock, setCurrentStock] = useState<number | null>(null);
    const [loadingStock, setLoadingStock] = useState<boolean>(false);

    useEffect(() => {
        fetchItems();
        fetchStores();
    }, []);

    const fetchItems = async () => {
        try {
            setLoading(true);
            const response = await ItemService.getItemsByInstitution(0, 100);
            setItems(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load items');
            console.error('Error fetching items:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchStores = async () => {
        try {
            setLoading(true);
            // Assuming you have a service to fetch stores
            const response = await getStoresByInstitutionId(); // Replace with actual service call
            setStores(response.content);
        } catch (err) {
            toast.showApiError(err, 'Failed to load stores');
            console.error('Error fetching stores:', err);
        } finally {
            setLoading(false);
        }
    }

    // Function to fetch current stock amount when both store and item are selected
    const fetchCurrentStock = async () => {
        if (selectedStoreId && selectedItemId) {
            try {
                setLoadingStock(true);
                setCurrentStock(null);
                const stockData = await StockService.getStockByStoreAndItem(selectedStoreId, selectedItemId);
                setCurrentStock(stockData.availableQty);
            } catch (err) {
                console.error('Error fetching current stock:', err);
                setCurrentStock(0); // Default to 0 if there's an error or no stock exists
            } finally {
                setLoadingStock(false);
            }
        }
    };

    // Fetch current stock when store or item selection changes
    useEffect(() => {
        fetchCurrentStock();
    }, [selectedStoreId, selectedItemId]);

    const handleItemChange = (event: SelectChangeEvent) => {
        const id = event.target.value;
        setSelectedItemId(id);

        const item = items.find(item => item.id === id);
        setSelectedItem(item || null);

        // Reset other fields
        setAmount(0);
    };

    const handleStoreChange = (event: SelectChangeEvent) => {
        const id = event.target.value;
        setSelectedStoreId(id);

        const store = stores.find(store => store.id === id);
        setSelectedStore(store || null);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedItemId) {
            toast.showError('Please select an item');
            return;
        }

        if (!selectedStoreId) {
            toast.showError('Please select a store');
            return;
        }

        if (amount <= 0) {
            toast.showError('Please enter a valid amount (greater than 0)');
            return;
        }

        if (currentStock !== null && amount > currentStock) {
            toast.showError(`Stock out amount cannot be greater than available quantity in this store (${currentStock})`);
            return;
        }

        try {
            const response = await StockService.stockOut({storeId: selectedStoreId, itemId: selectedItemId, amount: amount, remarks: remarks || undefined});

            // Refresh the current stock after successful stock out
            fetchCurrentStock();

            toast.showSuccess(`Stock out successful. New quantity: ${response.availableQty}`);

            // Reset form fields but keep selections
            setAmount(0);
            setRemarks('');
        } catch (error) {
            console.error('Error during stock out:', error);
            toast.showApiError(error, 'Failed to process stock out');
        }
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }}>
            <Typography variant="h4" gutterBottom>
                Stock Out
            </Typography>

            <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 500 }}>
                <FormControl fullWidth margin="normal" required>
                    <InputLabel>Select Item</InputLabel>
                    <Select
                        value={selectedItemId}
                        label="Select Item"
                        onChange={handleItemChange}
                        data-testid="item-select"
                    >
                        {items.map((item) => (
                            <MenuItem key={item.id} value={item.id}>
                                {item.itemName} - Category: {item.itemCategory?.itemCategoryName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl fullWidth margin="normal" required>
                    <InputLabel>Select Store</InputLabel>
                    <Select
                        value={selectedStoreId}
                        label="Select Store"
                        onChange={handleStoreChange}
                        data-testid="store-select"
                    >
                        {stores.map((store) => (
                            <MenuItem key={store.id} value={store.id}>
                                {store.storeName}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {selectedItem && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Item Details:
                        </Typography>
                        <Typography>
                            <strong>Name:</strong> {selectedItem.itemName}
                        </Typography>
                        <Typography>
                            <strong>Category:</strong> {selectedItem.itemCategory?.itemCategoryName}
                        </Typography>
                        <Typography>
                            <strong>Description:</strong> {selectedItem.description}
                        </Typography>
                    </Box>
                )}

                {selectedStore && (
                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                        <Typography variant="subtitle1" gutterBottom>
                            Store Details:
                        </Typography>
                        <Typography>
                            <strong>Name:</strong> {selectedStore.storeName}
                        </Typography>
                        <Typography>
                            <strong>Description:</strong> {selectedStore.description}
                        </Typography>
                        <Typography>
                            <strong>Address:</strong> {selectedStore.address}
                        </Typography>
                    </Box>
                )}

                {selectedStore && selectedItem && (
                    <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: '#e3f2fd', borderRadius: 1, border: '1px solid #bbdefb' }}>
                        {loadingStock ? (
                            <Typography>Loading current stock information...</Typography>
                        ) : (
                            <Typography variant="subtitle1" fontWeight="medium">
                                Current Stock: {currentStock !== null ? currentStock : 'Not available'}
                                {selectedItem?.unit ? ` ${selectedItem.unit.symbol}` : ''}
                            </Typography>
                        )}
                    </Box>
                )}

                <TextField
                    fullWidth
                    label={`Stock Out Amount${selectedItem?.unit ? ` (${selectedItem.unit.symbol})` : ''}`}
                    type="number"
                    value={amount}
                    onChange={(e) => {
                        const value = Number(e.target.value);
                        if (value >= 0) {
                            setAmount(value);
                        }
                    }}
                    required
                    margin="normal"
                    InputProps={{
                        inputProps: {
                            min: 0,
                            max: currentStock || 0
                        }
                    }}
                    disabled={!selectedItem || !selectedStoreId}
                    data-testid="amount-input"
                />

                <TextField
                    fullWidth
                    label="Remarks (optional)"
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    margin="normal"
                    multiline
                    rows={2}
                    disabled={!selectedItem || !selectedStoreId}
                    data-testid="remarks-input"
                    inputProps={{ maxLength: TEXT_AREA_MAX_LENGTH }}
                    helperText={`${remarks.length}/${TEXT_AREA_MAX_LENGTH}`}
                />

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    sx={{ mt: 2 }}
                    disabled={!selectedItem || !selectedStoreId}
                    data-testid="submit-button"
                >
                    Stock Out
                </Button>
            </Box>
        </div>
    );
};

export default StockOut;
